// HeaderActions — iter144 redesign.
// ⚠️ DO NOT DUPLICATE — shared header icons component ⚠️
//
// The 3-icon cluster (Friends / Alerts / Account) used in the top-right of
// every primary page header (Discover, Journey, Connect, Ride, Friends, …).
//
// Spec (iter144):
//   • Each icon is a column: circle button + small gold label underneath.
//   • Tappable area is at least 48px (extra padding around the visible circle).
//   • Labels: DM Sans 9px / 500 / #E8B84B / centered / 0.3px letter-spacing.
//   • Bilingual labels follow the app language.
//
// Friends — 34px circle, gold-tint bg, Users icon at 15px.
// Alerts  — 34px circle (same chrome) + 16px gold notification badge.
//           Clicking opens the NotificationBell dropdown (logic unchanged).
// Account — 36px circle. Authed: gold gradient + user initial. Guest:
//           gold-tint bg + User icon. Tap opens the ProfileDropdown menu.
//
// Also exported as `HeaderIcons` (named) so future call sites can prefer
// the more descriptive name without breaking existing `HeaderActions`
// imports.
import { Link } from "react-router-dom";
import { Users } from "lucide-react";
import { NotificationBell } from "./NotificationBell";
import ProfileDropdown from "./ProfileDropdown";
import { useI18n } from "../lib/i18n";

const LABEL_STYLE = {
  fontFamily: "'DM Sans', sans-serif",
  fontSize: 9,
  fontWeight: 500,
  color: "#E8B84B",
  letterSpacing: "0.3px",
  textAlign: "center",
  marginTop: 3,
  whiteSpace: "nowrap",
};

const COLUMN_STYLE = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  minHeight: 48,
  justifyContent: "flex-end",
};

function FriendsColumn({ testidPrefix }) {
  const { lang } = useI18n();
  return (
    <Link
      to="/friends"
      data-testid={`${testidPrefix}-friends-link`}
      aria-label={lang === "es" ? "Amigos" : "Friends"}
      style={COLUMN_STYLE}
      className="transition-opacity duration-200 hover:opacity-80"
    >
      <span
        className="grid place-items-center rounded-full"
        style={{
          width: 34,
          height: 34,
          background: "rgba(232,184,75,0.12)",
          border: "1px solid rgba(232,184,75,0.35)",
          color: "#E8B84B",
        }}
      >
        <Users style={{ width: 15, height: 15 }} strokeWidth={1.6} />
      </span>
      <span style={LABEL_STYLE} data-testid={`${testidPrefix}-friends-label`}>
        {lang === "es" ? "Amigos" : "Friends"}
      </span>
    </Link>
  );
}

export default function HeaderActions({ user, testidPrefix = "header" }) {
  return (
    <div
      className="flex items-end"
      style={{ gap: 10 }}
      data-testid={`${testidPrefix}-actions`}
    >
      <FriendsColumn testidPrefix={testidPrefix} />
      <NotificationBell labeled testidPrefix={testidPrefix} />
      <ProfileDropdown user={user} labeled testidPrefix={testidPrefix} />
    </div>
  );
}

// Named alias per the iter144 spec (HeaderIcons.jsx). Both names point to
// the same component so callers can adopt either name without churn.
export { HeaderActions as HeaderIcons };
